import { Plugin, Widget } from 'ckeditor5'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { toWidget } from 'ckeditor5'
import { Wordle } from '@minicoursegenerator/wordle'

// Transform config - API data is already in correct format, just pass through
const transformConfigForWordle = (config) => {
    if (!config) return config
    return { ...config }
}

const DEFAULT_WORDLE_JSON = {
    questTitle: 'Word Quest',
    language: 'en',
    colors: {
        primary: '#4f46e5',
        background: '#ffffff',
        cardBackground: '#ffffff',
        textMain: '#0f172a',
        textSecondary: '#64748b',
        accent: '#6366f1',
        success: '#059669'
    },
    words: [
        {
            word: 'HELLO',
            hint: 'A common greeting'
        }
    ]
}

export default class WordlePlugin extends Plugin {
    static get pluginName() {
        return 'WordlePlugin'
    }

    static get requires() {
        return [Widget]
    }

    constructor(editor) {
        super(editor)
        this.mountedRoots = new Map()
        this._updateTimeout = null
        this._lastConfigStr = null
    }

    init() {
        const editor = this.editor

        editor.model.schema.register('wordle', {
            isObject: true,
            allowWhere: '$block',
            allowIn: 'column',
            allowAttributes: [
                'id',
                'data',
                'gameConfig',
            ],
        })

        this._defineConverters()
        this._setupAttributeListener()

        editor.ui.componentFactory.add('insertWordle', locale => {
            const button = document.createElement('button')
            button.className = 'ck-button ck-button_with-text'
            button.style.display = 'flex'
            button.style.alignItems = 'center'
            button.style.gap = '4px'
            button.style.padding = '8px 12px'

            const icon = document.createElement('span')
            icon.textContent = '🔤'
            icon.style.fontSize = '16px'

            const text = document.createTextNode('Wordle')

            button.appendChild(icon)
            button.appendChild(text)

            button.onclick = () => {
                editor.execute('insertWordle')
            }

            return {
                element: button,
                render: () => { },
                destroy: () => { },
            }
        })

        // Command to insert via button (uses default JSON)
        editor.commands.add('insertWordle', {
            execute: () => {
                editor.model.change(writer => {
                    const wordleLanguage = editor.wordleLanguage || 'en'
                    const newConfig = JSON.parse(JSON.stringify(DEFAULT_WORDLE_JSON))
                    if (newConfig.config) {
                        newConfig.config.language = wordleLanguage
                    }

                    // Get bgColor from config or colors.background
                    const bgColor = newConfig?.config?.bgColor || newConfig?.colors?.background || '#ffffff'

                    const simpleBoxWrapper = writer.createElement('simpleBoxWrapper', {
                        backgroundColor: bgColor,
                    })
                    const simpleBox = writer.createElement('simpleBox')
                    const column = writer.createElement('column')
                    const wordle = writer.createElement('wordle', {
                        gameConfig: JSON.stringify(newConfig),
                    })

                    writer.append(simpleBox, simpleBoxWrapper)
                    writer.append(column, simpleBox)
                    writer.append(wordle, column)

                    editor.model.insertContent(simpleBoxWrapper)
                    writer.setSelection(wordle, 'on')
                })
            },
        })
    }

    _setupAttributeListener() {
        const editor = this.editor

        editor.model.document.on('change:data', () => {
            const changes = editor.model.document.differ.getChanges()

            for (const change of changes) {
                if (change.type === 'attribute' && change.attributeKey === 'gameConfig') {
                    const modelElement = change.range.start.nodeAfter
                    if (modelElement && modelElement.name === 'wordle') {
                        const newGameConfig = modelElement.getAttribute('gameConfig')
                        this._updateWordleComponent(modelElement, newGameConfig)
                    }
                }
            }
        })
    }

    _updateWordleComponent(modelElement, gameConfig) {
        if (gameConfig === this._lastConfigStr) {
            return
        }

        this._lastConfigStr = gameConfig

        if (this._updateTimeout) {
            clearTimeout(this._updateTimeout)
        }

        this._updateTimeout = setTimeout(() => {
            const editor = this.editor

            const viewElement = editor.editing.mapper.toViewElement(modelElement)
            if (!viewElement) return

            const domConverter = editor.editing.view.domConverter
            const containerDom = domConverter.mapViewToDom(viewElement)
            if (!containerDom) return

            const placeholder = containerDom.querySelector('.wordle-react-placeholder')
            if (!placeholder) return

            const existingRoot = this.mountedRoots.get(placeholder)
            if (existingRoot) {
                try {
                    const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_WORDLE_JSON
                    const transformedConfig = transformConfigForWordle(parsedConfig)
                    const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.wordleLanguage || 'en'

                    const props = {
                        data: transformedConfig,
                        language: configLanguage,
                        onComplete: () => console.log('🔤 Wordle Solved!'),
                        onFail: () => console.log('🔤 Wordle Failed!'),
                        onChange: state => console.log('🔤 Wordle State:', state),
                    }

                    console.log('🔤 Wordle re-rendering with new config:', props)
                    existingRoot.render(React.createElement(Wordle, props))
                } catch (error) {
                    console.error('Error re-rendering Wordle component:', error)
                }
            }
        }, 500)
    }

    _defineConverters() {
        const editor = this.editor
        const conversion = editor.conversion

        conversion.for('upcast').elementToElement({
            view: {
                name: 'div',
                attributes: {
                    'data-type': 'wordle',
                },
            },
            model: (viewElement, { writer }) => {
                const id = viewElement.getAttribute('id')
                const data = viewElement.getAttribute('data') || ''
                const gameConfig = viewElement.getAttribute('data-game-config') || ''

                const attrs = {
                    data,
                    gameConfig,
                }
                if (id) attrs.id = id

                return writer.createElement('wordle', attrs)
            },
        })

        conversion.for('dataDowncast').elementToElement({
            model: 'wordle',
            view: (modelElement, { writer }) => {
                const id = modelElement.getAttribute('id')
                const data = modelElement.getAttribute('data') || ''
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const attrs = {
                    'data-type': 'wordle',
                    data: data,
                    'data-game-config': gameConfig,
                }

                if (id !== undefined && id !== null) {
                    attrs.id = id
                }

                return writer.createEmptyElement('div', attrs)
            },
        })

        conversion.for('editingDowncast').elementToElement({
            model: 'wordle',
            view: (modelElement, { writer }) => {
                const gameConfig = modelElement.getAttribute('gameConfig') || ''

                const containerAttrs = {
                    class: 'wordle-widget-container',
                    'data-type': 'wordle',
                    'data-game-config': gameConfig,
                }

                const container = writer.createContainerElement('div', containerAttrs)

                const placeholder = writer.createRawElement(
                    'div',
                    {
                        class: 'wordle-react-placeholder',
                    },
                    domElement => {
                        const stopPropagation = e => e.stopPropagation()
                        const events = ['mousedown', 'click', 'dblclick', 'pointerdown', 'pointermove', 'pointerup', 'keydown', 'keyup', 'input']
                        events.forEach(evt => domElement.addEventListener(evt, stopPropagation))
                        domElement.addEventListener('focus', stopPropagation, true)
                        domElement.addEventListener('blur', stopPropagation, true)

                        console.log('🔤 Wordle rendering started')
                        try {
                            const parsedConfig = gameConfig ? JSON.parse(gameConfig) : DEFAULT_WORDLE_JSON
                            const transformedConfig = transformConfigForWordle(parsedConfig)
                            const configLanguage = transformedConfig?.config?.language || transformedConfig?.language || editor.wordleLanguage || 'en'

                            const props = {
                                data: transformedConfig,
                                language: configLanguage,
                                onComplete: () => console.log('🔤 Wordle Solved!'),
                                onFail: () => console.log('🔤 Wordle Failed!'),
                                onChange: state => console.log('🔤 Wordle State:', state),
                            }

                            console.log('🔤 Wordle Props (Initial):', props)

                            const root = createRoot(domElement)
                            root.render(React.createElement(Wordle, props))

                            editor.plugins
                                .get('WordlePlugin')
                                .mountedRoots.set(domElement, root)
                        } catch (error) {
                            console.error('Error rendering Wordle component:', error)
                            domElement.innerHTML = `
                <div style="padding: 20px; background: #f5f5f5; border-radius: 8px; text-align: center;">
                  <span style="font-size: 24px;">🔤</span>
                  <p>Wordle</p>
                  <small>Component not available</small>
                </div>
              `
                        }
                    },
                )

                writer.insert(writer.createPositionAt(container, 0), placeholder)

                return toWidget(container, writer, {
                    label: 'Wordle',
                    hasSelectionHandle: true,
                })
            },
        })
    }
}
